import React, { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import AdminLayout from "@/components/layouts/AdminLayout";
import CloudinaryUpload from "@/components/CloudinaryUpload";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Package, Plus, CheckCircle, X, ExternalLink, Trash2, ChevronDown, TrendingDown, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { LinkImporter } from "@/components/admin/LinkImporter";
import { CompareOffersDialog } from "@/components/admin/CompareOffersDialog";
import { ProductEditDialog } from "@/components/admin/ProductEditDialog";
import { PriceRefreshBar } from "@/components/admin/PriceRefreshBar";
import { Pencil } from "lucide-react";

const AdminProducts = () => {
  const { getAuthHeaders } = useAuth();
  const [affiliateProducts, setAffiliateProducts] = useState([]);
  const [pendingProducts, setPendingProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [priceCompareOpen, setPriceCompareOpen] = useState(false);
  const [compareProduct, setCompareProduct] = useState(null);
  const [editProduct, setEditProduct] = useState(null);
  const [form, setForm] = useState({
    brand_name: "",
    title: "",
    description: "",
    image_url: "",
    affiliate_link: "",
    category: "",
    original_price: "",
    discounted_price: "",
    commission_percent: "",
    cashback_percent: "",
    // Price comparison fields
    flipkart_price: "",
    flipkart_url: "",
    amazon_price: "",
    amazon_url: "",
    other_platform_name: "",
    other_platform_price: "",
    other_platform_url: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [affRes, pendRes, catRes] = await Promise.all([
        axios.get(`${API}/products/affiliate?limit=100`),
        axios.get(`${API}/admin/products/pending`, { headers: getAuthHeaders() }),
        axios.get(`${API}/categories`),
      ]);
      setAffiliateProducts(affRes.data.products || []);
      setPendingProducts(pendRes.data.products || []);
      setCategories(catRes.data.categories || []);
    } catch (error) {
      toast.error("Failed to load products");
    }
    setLoading(false);
  };

  const resetForm = () => {
    setForm({
      brand_name: "",
      title: "",
      description: "",
      image_url: "",
      affiliate_link: "",
      category: "",
      original_price: "",
      discounted_price: "",
      commission_percent: "",
      cashback_percent: "",
      flipkart_price: "",
      flipkart_url: "",
      amazon_price: "",
      amazon_url: "",
      other_platform_name: "",
      other_platform_price: "",
      other_platform_url: "",
    });
    setPriceCompareOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    // Build price comparison object if any prices are provided
    const priceComparison = {};
    if (form.flipkart_price) {
      priceComparison.flipkart_price = parseFloat(form.flipkart_price);
      priceComparison.flipkart_url = form.flipkart_url || null;
    }
    if (form.amazon_price) {
      priceComparison.amazon_price = parseFloat(form.amazon_price);
      priceComparison.amazon_url = form.amazon_url || null;
    }
    if (form.other_platform_price && form.other_platform_name) {
      priceComparison.other_platform_name = form.other_platform_name;
      priceComparison.other_platform_price = parseFloat(form.other_platform_price);
      priceComparison.other_platform_url = form.other_platform_url || null;
    }

    try {
      await axios.post(`${API}/admin/products/affiliate`, {
        brand_name: form.brand_name,
        title: form.title,
        description: form.description,
        image_url: form.image_url,
        affiliate_link: form.affiliate_link,
        category: form.category,
        original_price: parseFloat(form.original_price),
        discounted_price: form.discounted_price ? parseFloat(form.discounted_price) : null,
        commission_percent: parseFloat(form.commission_percent),
        cashback_percent: parseFloat(form.cashback_percent),
        price_comparison: Object.keys(priceComparison).length > 0 ? priceComparison : null,
      }, { headers: getAuthHeaders() });
      toast.success("Affiliate product created!");
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("Failed to create product");
    }
    setSubmitting(false);
  };

  const approveProduct = async (productId) => {
    try {
      await axios.put(`${API}/admin/products/${productId}/approve`, {}, {
        headers: getAuthHeaders(),
      });
      toast.success("Product approved");
      fetchData();
    } catch (error) {
      toast.error("Failed to approve product");
    }
  };

  const rejectProduct = async (productId) => {
    try {
      await axios.put(`${API}/admin/products/${productId}/reject`, {}, {
        headers: getAuthHeaders(),
      });
      toast.success("Product rejected");
      fetchData();
    } catch (error) {
      toast.error("Failed to reject product");
    }
  };

  const deleteAffiliateProduct = async (productId) => {
    if (!confirm("Delete this affiliate product?")) return;
    try {
      await axios.delete(`${API}/admin/products/affiliate/${productId}`, {
        headers: getAuthHeaders(),
      });
      toast.success("Product deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete product");
    }
  };

  const getMVenturesPrice = (product) => {
    return product.discounted_price || product.original_price;
  };

  const getBestPrice = (product) => {
    const prices = [getMVenturesPrice(product)];
    if (product.price_comparison?.flipkart_price) prices.push(product.price_comparison.flipkart_price);
    if (product.price_comparison?.amazon_price) prices.push(product.price_comparison.amazon_price);
    if (product.price_comparison?.other_platform_price) prices.push(product.price_comparison.other_platform_price);
    return Math.min(...prices);
  };

  const isMVenturesCheapest = (product) => {
    const mvPrice = getMVenturesPrice(product);
    return mvPrice === getBestPrice(product);
  };

  return (
    <AdminLayout title="Products">
      <Tabs defaultValue="affiliate" className="space-y-6">
        <TabsList>
          <TabsTrigger value="affiliate">Affiliate Products</TabsTrigger>
          <TabsTrigger value="pending">
            Pending Approval
            {pendingProducts.length > 0 && (
              <Badge className="ml-2 bg-yellow-100 text-yellow-700">
                {pendingProducts.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="affiliate">
          <LinkImporter categories={categories} onSaved={fetchData} />
          <CompareOffersDialog
            key={compareProduct?.id}
            product={compareProduct}
            open={!!compareProduct}
            onOpenChange={(o) => !o && setCompareProduct(null)}
            onChanged={fetchData}
          />
          <ProductEditDialog
            key={`edit-${editProduct?.id}`}
            product={editProduct}
            open={!!editProduct}
            onOpenChange={(o) => !o && setEditProduct(null)}
            categories={categories}
            onSaved={fetchData}
          />
          <PriceRefreshBar onDone={fetchData} />
          <div className="flex justify-between items-center mb-6">
            <p className="text-gray-500">{affiliateProducts.length} affiliate products</p>
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-secondary-500 !text-primary-900 hover:bg-secondary-400" data-testid="add-affiliate-product-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Affiliate Product
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Add Affiliate Product</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Brand Name</Label>
                      <Input
                        value={form.brand_name}
                        onChange={(e) => setForm({ ...form, brand_name: e.target.value })}
                        placeholder="e.g., Ajio"
                        required
                        data-testid="brand-name-input"
                      />
                    </div>
                    <div>
                      <Label>Category</Label>
                      <Select
                        value={form.category}
                        onValueChange={(v) => setForm({ ...form, category: v })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map((cat) => (
                            <SelectItem key={cat.id} value={cat.name}>
                              {cat.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>Product Title</Label>
                    <Input
                      value={form.title}
                      onChange={(e) => setForm({ ...form, title: e.target.value })}
                      placeholder="Enter product title"
                      required
                      data-testid="product-title-input"
                    />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      placeholder="Enter description"
                      required
                    />
                  </div>
                  <div>
                    <Label>Product Image</Label>
                    <CloudinaryUpload
                      value={form.image_url}
                      onChange={(url) => setForm({ ...form, image_url: url })}
                      folder="affiliate_products/"
                      resourceType="image"
                      testId="admin-aff-product-image-upload"
                    />
                  </div>
                  <div>
                    <Label>Affiliate Link</Label>
                    <Input
                      value={form.affiliate_link}
                      onChange={(e) => setForm({ ...form, affiliate_link: e.target.value })}
                      placeholder="https://..."
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Original Price (₹)</Label>
                      <Input
                        type="number"
                        value={form.original_price}
                        onChange={(e) => setForm({ ...form, original_price: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label>M Ventures Price (₹)</Label>
                      <Input
                        type="number"
                        value={form.discounted_price}
                        onChange={(e) => setForm({ ...form, discounted_price: e.target.value })}
                        placeholder="Leave blank if same as original"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Commission % (Platform)</Label>
                      <Input
                        type="number"
                        value={form.commission_percent}
                        onChange={(e) => setForm({ ...form, commission_percent: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label>Cashback % (User)</Label>
                      <Input
                        type="number"
                        value={form.cashback_percent}
                        onChange={(e) => setForm({ ...form, cashback_percent: e.target.value })}
                        required
                      />
                    </div>
                  </div>

                  {/* Price Comparison Section */}
                  <Collapsible open={priceCompareOpen} onOpenChange={setPriceCompareOpen}>
                    <CollapsibleTrigger asChild>
                      <Button type="button" variant="outline" className="w-full justify-between">
                        <span className="flex items-center gap-2">
                          <BarChart3 className="w-4 h-4" />
                          Price Comparison (Optional)
                        </span>
                        <ChevronDown className={`w-4 h-4 transition-transform ${priceCompareOpen ? 'rotate-180' : ''}`} />
                      </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-4 space-y-4 p-4 bg-gray-50 rounded-xl">
                      <p className="text-sm text-gray-500">
                        Add competitor prices to show users the best deal
                      </p>
                      
                      {/* Flipkart */}
                      <div className="p-3 bg-white rounded-lg border">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-6 h-6 bg-[#2874F0] rounded flex items-center justify-center">
                            <span className="text-white text-xs font-bold">F</span>
                          </div>
                          <span className="font-medium">Flipkart</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-xs">Price (₹)</Label>
                            <Input
                              type="number"
                              value={form.flipkart_price}
                              onChange={(e) => setForm({ ...form, flipkart_price: e.target.value })}
                              placeholder="Flipkart price"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Product URL</Label>
                            <Input
                              value={form.flipkart_url}
                              onChange={(e) => setForm({ ...form, flipkart_url: e.target.value })}
                              placeholder="https://flipkart.com/..."
                            />
                          </div>
                        </div>
                      </div>

                      {/* Amazon */}
                      <div className="p-3 bg-white rounded-lg border">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-6 h-6 bg-[#FF9900] rounded flex items-center justify-center">
                            <span className="text-black text-xs font-bold">A</span>
                          </div>
                          <span className="font-medium">Amazon</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-xs">Price (₹)</Label>
                            <Input
                              type="number"
                              value={form.amazon_price}
                              onChange={(e) => setForm({ ...form, amazon_price: e.target.value })}
                              placeholder="Amazon price"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Product URL</Label>
                            <Input
                              value={form.amazon_url}
                              onChange={(e) => setForm({ ...form, amazon_url: e.target.value })}
                              placeholder="https://amazon.in/..."
                            />
                          </div>
                        </div>
                      </div>

                      {/* Other Platform */}
                      <div className="p-3 bg-white rounded-lg border">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-6 h-6 bg-gray-500 rounded flex items-center justify-center">
                            <span className="text-white text-xs font-bold">+</span>
                          </div>
                          <span className="font-medium">Other Platform</span>
                        </div>
                        <div className="mb-2">
                          <Label className="text-xs">Platform Name</Label>
                          <Input
                            value={form.other_platform_name}
                            onChange={(e) => setForm({ ...form, other_platform_name: e.target.value })}
                            placeholder="e.g., Myntra, Nykaa"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-xs">Price (₹)</Label>
                            <Input
                              type="number"
                              value={form.other_platform_price}
                              onChange={(e) => setForm({ ...form, other_platform_price: e.target.value })}
                              placeholder="Price"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Product URL</Label>
                            <Input
                              value={form.other_platform_url}
                              onChange={(e) => setForm({ ...form, other_platform_url: e.target.value })}
                              placeholder="https://..."
                            />
                          </div>
                        </div>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>

                  <Button
                    type="submit"
                    className="w-full bg-secondary-500 !text-primary-900 hover:bg-secondary-400"
                    disabled={submitting}
                    data-testid="submit-affiliate-product-btn"
                  >
                    {submitting ? "Creating..." : "Create Product"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-0">
              {loading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto" />
                </div>
              ) : affiliateProducts.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Product</TableHead>
                        <TableHead>Brand</TableHead>
                        <TableHead>M Ventures Price</TableHead>
                        <TableHead>Price Comparison</TableHead>
                        <TableHead>Cashback</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {affiliateProducts.map((product) => (
                        <TableRow key={product.id}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <img
                                src={product.image_url}
                                alt={product.title}
                                className="w-12 h-12 object-cover rounded-lg"
                              />
                              <div className="max-w-[200px]">
                                <p className="font-medium truncate">{product.title}</p>
                                <p className="text-xs text-gray-400">{product.category}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>{product.brand_name}</TableCell>
                          <TableCell>
                            <div>
                              <p className="font-bold text-primary-600">₹{getMVenturesPrice(product)}</p>
                              {product.discounted_price && (
                                <p className="text-xs text-gray-400 line-through">₹{product.original_price}</p>
                              )}
                              {isMVenturesCheapest(product) && product.price_comparison && (
                                <Badge className="mt-1 bg-green-100 text-green-700 text-xs">
                                  <TrendingDown className="w-3 h-3 mr-1" />
                                  Lowest Price
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {(product.compare_offers || []).length > 0 && (
                              <div className="space-y-1 text-xs mb-1" data-testid={`admin-offers-${product.id}`}>
                                {product.compare_offers.map((o) => (
                                  <div key={o.id} className="flex items-center gap-1">
                                    <span className="text-gray-500">{o.site}:</span>
                                    <span className={getMVenturesPrice(product) < o.price ? "text-gray-500 line-through" : "text-red-600"}>₹{o.price}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                            {product.price_comparison ? (
                              <div className="space-y-1 text-xs">
                                {product.price_comparison.flipkart_price && (
                                  <div className="flex items-center gap-1">
                                    <span className="text-gray-500">Flipkart:</span>
                                    <span className={getMVenturesPrice(product) < product.price_comparison.flipkart_price ? 'text-gray-500 line-through' : 'text-red-600'}>
                                      ₹{product.price_comparison.flipkart_price}
                                    </span>
                                  </div>
                                )}
                                {product.price_comparison.amazon_price && (
                                  <div className="flex items-center gap-1">
                                    <span className="text-gray-500">Amazon:</span>
                                    <span className={getMVenturesPrice(product) < product.price_comparison.amazon_price ? 'text-gray-500 line-through' : 'text-red-600'}>
                                      ₹{product.price_comparison.amazon_price}
                                    </span>
                                  </div>
                                )}
                                {product.price_comparison.other_platform_name && (
                                  <div className="flex items-center gap-1">
                                    <span className="text-gray-500">{product.price_comparison.other_platform_name}:</span>
                                    <span className={getMVenturesPrice(product) < product.price_comparison.other_platform_price ? 'text-gray-500 line-through' : 'text-red-600'}>
                                      ₹{product.price_comparison.other_platform_price}
                                    </span>
                                  </div>
                                )}
                              </div>
                            ) : !(product.compare_offers || []).length && (
                              <span className="text-gray-400 text-xs">Not added</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge className="bg-green-100 text-green-700">
                              {product.cashback_percent}%
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <a
                                href={product.affiliate_link}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <Button size="sm" variant="outline">
                                  <ExternalLink className="w-4 h-4" />
                                </Button>
                              </a>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setEditProduct(product)}
                                data-testid={`edit-product-btn-${product.id}`}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setCompareProduct(product)}
                                data-testid={`compare-offers-btn-${product.id}`}
                              >
                                <BarChart3 className="w-4 h-4 mr-1" />{(product.compare_offers || []).length}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600"
                                onClick={() => deleteAffiliateProduct(product.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="p-12 text-center">
                  <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No affiliate products yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pending">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle>Seller Products Pending Approval</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {pendingProducts.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Product</TableHead>
                        <TableHead>Seller</TableHead>
                        <TableHead>Price</TableHead>
                        <TableHead>Price Comparison</TableHead>
                        <TableHead>Commission</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {pendingProducts.map((product) => (
                        <TableRow key={product.id}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              {product.images?.[0] && (
                                <img
                                  src={product.images[0]}
                                  alt={product.name}
                                  className="w-12 h-12 object-cover rounded-lg"
                                />
                              )}
                              <div>
                                <p className="font-medium">{product.name}</p>
                                <p className="text-xs text-gray-500">{product.category}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>{product.seller_name}</TableCell>
                          <TableCell>₹{product.price}</TableCell>
                          <TableCell>
                            {product.price_comparison ? (
                              <div className="space-y-1 text-xs">
                                {product.price_comparison.flipkart_price && (
                                  <div>Flipkart: ₹{product.price_comparison.flipkart_price}</div>
                                )}
                                {product.price_comparison.amazon_price && (
                                  <div>Amazon: ₹{product.price_comparison.amazon_price}</div>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400 text-xs">Not added</span>
                            )}
                          </TableCell>
                          <TableCell>{product.commission_percent}%</TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={() => approveProduct(product.id)}
                                className="bg-green-500 hover:bg-green-600 text-white"
                                data-testid={`approve-product-${product.id}`}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => rejectProduct(product.id)}
                                className="text-red-600"
                              >
                                <X className="w-4 h-4 mr-1" />
                                Reject
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="p-12 text-center">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <p className="text-gray-500">No products pending approval</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </AdminLayout>
  );
};

export default AdminProducts;
